import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import Notification from '../common/Notification';
import { FaBalanceScale, FaSpinner, FaLock, FaUnlock, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import '../../styles/Reconciliation.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function Reconciliation() {
  const { user } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalStaffSalary, setTotalStaffSalary] = useState(0);
  const [expenditures, setExpenditures] = useState([]);
  const [otherIncome, setOtherIncome] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryDistribution, setSalaryDistribution] = useState({});
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [prevMonthOutstanding, setPrevMonthOutstanding] = useState({});
  const [classDiscounts, setClassDiscounts] = useState({});
  const [outstandingOverrides, setOutstandingOverrides] = useState({});
  const [openingBalance, setOpeningBalance] = useState(null);
  const [savedBalance, setSavedBalance] = useState(null);
  const [savedExpenditure, setSavedExpenditure] = useState(null);
  const [savedIncome, setSavedIncome] = useState(null);
  const [closedMonths, setClosedMonths] = useState([]);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFillModal, setShowFillModal] = useState(false);
  const [fillText, setFillText] = useState('');
  const [expSort, setExpSort] = useState({ field: null, direction: 'asc' });

  const isClosed = savedBalance !== null;

  const lastClosed = useMemo(() => {
    if (!closedMonths || closedMonths.length === 0) return null;
    const parsed = closedMonths
      .map(key => { const [y, m] = key.split('-').map(v => parseInt(v, 10)); return { year: y, month: m, key }; })
      .filter(x => !isNaN(x.year) && !isNaN(x.month));
    if (parsed.length === 0) return null;
    parsed.sort((a, b) => (a.year - b.year) || (a.month - b.month));
    return parsed[parsed.length - 1];
  }, [closedMonths]);

  useEffect(() => { if (selectedMonth && selectedYear) fetchData(); }, [selectedMonth, selectedYear]);
  useEffect(() => { fetchClosedMonths(); }, []);

  const fetchClosedMonths = async () => {
    try {
      const { data, error } = await supabase.from('reconciliation').select('year, month, balance').not('balance', 'is', null);
      if (!error && data) {
        const closed = data.filter(r => r.balance && r.balance._closed === true);
        const closedSet = new Set(closed.map(r => `${r.year}-${r.month}`));
        setClosedMonths(Array.from(closedSet));

        // Find the earliest not-closed month (the month right after the last closed one)
        if (closedSet.size === 0) {
          // No months closed, default to current month
          setSelectedMonth(now.getMonth() + 1);
          setSelectedYear(now.getFullYear());
        } else {
          // Find the latest closed month, then go to the next one
          const closedParsed = Array.from(closedSet).map(k => {
            const [y, m] = k.split('-').map(Number);
            return { year: y, month: m };
          });
          closedParsed.sort((a, b) => (a.year - b.year) || (a.month - b.month));
          const latest = closedParsed[closedParsed.length - 1];
          let nextMonth = latest.month + 1;
          let nextYear = latest.year;
          if (nextMonth > 12) { nextMonth = 1; nextYear++; }
          setSelectedMonth(nextMonth);
          setSelectedYear(nextYear);
        }
      }
    } catch (err) { console.error('Error fetching closed months:', err); }
  };

  const fetchData = async () => {
    if (!selectedMonth || !selectedYear) return;
    setLoading(true);
    setOutstandingOverrides({});
    try {
      const { data: reconRecord } = await supabase.from('reconciliation').select('balance, expenditure').eq('year', selectedYear).eq('month', selectedMonth).single();

      // Opening balance: use saved value for current month if present, else compute from previous month
      if (reconRecord?.balance?._openingBalance !== undefined) {
        setOpeningBalance(reconRecord.balance._openingBalance);
      } else {
        const prevMonthNum = selectedMonth === 1 ? 12 : selectedMonth - 1;
        const prevYearNum = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
        const { data: prevMonthRecord } = await supabase.from('reconciliation')
          .select('balance, expenditure').eq('year', prevYearNum).eq('month', prevMonthNum).maybeSingle();
        if (prevMonthRecord?.balance?._closed) {
          const prevOpeningBalance = prevMonthRecord.balance._openingBalance || 0;
          const prevIncome = prevMonthRecord.balance._income
            ? Object.entries(prevMonthRecord.balance._income).reduce((s, [, v]) => s + (parseInt(v, 10) || 0), 0)
            : 0;
          const prevExp = prevMonthRecord.expenditure
            ? Object.entries(prevMonthRecord.expenditure).filter(([k]) => k !== 'distribution').reduce((s, [, v]) => s + (parseInt(v, 10) || 0), 0)
            : 0;
          setOpeningBalance(prevOpeningBalance + prevIncome - prevExp);
        } else {
          setOpeningBalance(null);
        }
      }

      if (reconRecord && reconRecord.balance && reconRecord.balance._closed === true) {
        setSavedBalance(reconRecord.balance);
        setSavedExpenditure(reconRecord.expenditure || {});
        setSavedIncome(reconRecord.balance?._income || {});
        setExpenditures([]); setOtherIncome([]); setClasses([]); setStudents([]); setTransactions([]); setTotalStaffSalary(0);
      } else {
        setSavedBalance(null); setSavedExpenditure(null); setSavedIncome(null);

        const { data: classesData, error: classErr } = await supabase.from('classes').select('id, class_name, fee, exam_fee, transport_fee').order('id', { ascending: true });
        if (classErr) throw classErr;

        const { data: studentsData, error: studErr } = await supabase.from('students').select('Rno, Name, Class, Active, Transport, "Transport Fee"');
        if (studErr) throw studErr;

        const { data: privateData, error: privErr } = await supabase.from('students_private_details').select('Rno, FeeDues');
        if (privErr) throw privErr;

        const mergedStudents = studentsData.map(s => {
          const priv = privateData.find(p => p.Rno === s.Rno);
          return { ...s, FeeDues: priv?.FeeDues || 0 };
        });

        const monthStart = new Date(selectedYear, selectedMonth - 1, 1).toISOString();
        const monthEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59).toISOString();
        const { data: txnData, error: txnErr } = await supabase.from('payment_transactions').select('student_rno, amount_paid, discount, examination_fee, other').gte('created_at', monthStart).lte('created_at', monthEnd);
        if (txnErr) throw txnErr;

        const { data: staffsData, error: staffErr } = await supabase.from('staffs').select('staff_no, name, salary, role').eq('is_active', true);
        if (staffErr) console.error('Error fetching staff salaries:', staffErr);
        const staffsList = (staffsData || []).map(s => ({ staff_no: s.staff_no, name: s.name, salary: parseInt(s.salary, 10) || 0, role: s.role || '' }));
        setStaffList(staffsList);
        setTotalStaffSalary(staffsList.reduce((sum, s) => sum + s.salary, 0));
        setDaysInMonth(30);

        // Initialize distribution: merge saved with current staff list
        const numDays = 30;
        const savedDist = (reconRecord && reconRecord.expenditure && reconRecord.expenditure.distribution) || {};
        const mergedDist = {};
        staffsList.forEach(s => {
          mergedDist[s.name] = { salary: s.salary, present: numDays, fine: 0, ...(savedDist[s.name] || {}) };
        });
        setSalaryDistribution(mergedDist);

        // Recalculate totalStaffSalary from distribution
        const distTotal = staffsList.reduce((sum, s) => {
          const d = mergedDist[s.name];
          const present = d.present || 0;
          // Rule: If 1 day leave or less (29 or 30 present), pay full salary (30 days)
          // If more than 1 day leave, pay for (present + 1) days
          const leaveDays = numDays - present;
          let payableDays;
          if (present >= numDays - 1) {
            // 29 or 30 days present: pay full salary
            payableDays = numDays;
          } else {
            // Less than 29 days present: pay for (present + 1) days
            payableDays = Math.min(numDays, present + 1);
          }
          const extra = parseInt(d.extra, 10) || 0;
          return sum + Math.round((d.salary || 0) / numDays * payableDays) - (parseInt(d.fine, 10) || 0) + extra;
        }, 0);
        setTotalStaffSalary(distTotal);

        // Fetch O/S data: use saved values from current month if available, else compute from previous month
        let classOutstanding = {};
        let hasSavedOS = false;

        if (reconRecord?.balance) {
          Object.entries(reconRecord.balance).forEach(([cls, data]) => {
            if (cls !== 'G. Total' && cls !== '_closed' && cls !== '_income' && data['O/s'] !== undefined) {
              classOutstanding[cls] = data['O/s'];
              hasSavedOS = true;
            }
          });
        }

        if (hasSavedOS) {
          // Restore any manual overrides that were saved
          setOutstandingOverrides(classOutstanding);
        }

        if (!hasSavedOS) {
          const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
          const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;

          const { data: prevRecon } = await supabase.from('reconciliation')
            .select('balance').eq('year', prevYear).eq('month', prevMonth).maybeSingle();

          if (prevRecon?.balance?._closed) {
            Object.entries(prevRecon.balance).forEach(([cls, data]) => {
              if (cls !== 'G. Total' && cls !== '_closed' && cls !== '_income') {
                classOutstanding[cls] = data['Balance'] || 0;
              }
            });
          } else {
            const sClassMap = {};
            studentsData.forEach(s => { sClassMap[s.Rno] = s.Class; });
            const studentOutstanding = {};

            const currStart = new Date(selectedYear, selectedMonth - 1, 1).toISOString();
            const currEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59).toISOString();
            const { data: currTxns } = await supabase.from('payment_transactions')
              .select('student_rno, fee_dues, created_at')
              .gte('created_at', currStart).lte('created_at', currEnd)
              .order('created_at', { ascending: true });

            if (currTxns) {
              currTxns.forEach(t => {
                if (studentOutstanding[t.student_rno] === undefined && t.fee_dues !== null && t.fee_dues !== undefined) {
                  studentOutstanding[t.student_rno] = parseFloat(t.fee_dues) || 0;
                }
              });
            }

            const { data: cronLogs } = await supabase.from('system_logs')
              .select('metadata')
              .eq('source', 'cron').eq('event', 'month_end_dues').eq('status', 'success')
              .order('created_at', { ascending: false });

            const currCronLog = cronLogs?.find(log =>
              log.metadata?.month === selectedMonth && log.metadata?.year === selectedYear
            );

            if (currCronLog?.metadata?.duesSnapshot) {
              currCronLog.metadata.duesSnapshot.forEach(s => {
                if (studentOutstanding[s.Rno] === undefined) {
                  studentOutstanding[s.Rno] = s.prevDues || 0;
                }
              });
            }

            Object.entries(studentOutstanding).forEach(([rno, dues]) => {
              const cls = sClassMap[parseInt(rno)];
              if (cls) {
                if (!classOutstanding[cls]) classOutstanding[cls] = 0;
                classOutstanding[cls] += dues;
              }
            });

            mergedStudents.forEach(s => {
              if (studentOutstanding[s.Rno] === undefined) {
                const cls = s.Class;
                if (cls) {
                  if (!classOutstanding[cls]) classOutstanding[cls] = 0;
                  classOutstanding[cls] += s.FeeDues;
                }
              }
            });
          }
        }

        setPrevMonthOutstanding(classOutstanding);

        // Calculate discounts per class
        const discountByClass = {};
        const discountByStudent = {};
        const sClassMap = {};
        studentsData.forEach(s => { sClassMap[s.Rno] = s.Class; });

        // Source 1: payment_transactions in current month → discount per student
        const currStart = new Date(selectedYear, selectedMonth - 1, 1).toISOString();
        const currEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59).toISOString();
        const { data: currTxns } = await supabase.from('payment_transactions')
          .select('student_rno, discount, created_at')
          .gte('created_at', currStart).lte('created_at', currEnd);

        if (currTxns) {
          currTxns.forEach(t => {
            if (t.discount !== null && t.discount !== undefined) {
              const discountVal = Math.abs(parseFloat(t.discount) || 0);
              if (!discountByStudent[t.student_rno]) discountByStudent[t.student_rno] = 0;
              discountByStudent[t.student_rno] += discountVal;
            }
          });
        }

        // Source 2: system_logs cron snapshot for current month → defaultDiscount for active students who didn't pay
        const { data: cronLogs } = await supabase.from('system_logs')
          .select('metadata')
          .eq('source', 'cron').eq('event', 'month_end_dues').eq('status', 'success')
          .order('created_at', { ascending: false });

        const currCronLog = cronLogs?.find(log =>
          log.metadata?.month === selectedMonth && log.metadata?.year === selectedYear
        );

        if (currCronLog?.metadata?.duesSnapshot) {
          currCronLog.metadata.duesSnapshot.forEach(s => {
            if (discountByStudent[s.Rno] === undefined && s.defaultDiscount) {
              discountByStudent[s.Rno] = parseFloat(s.defaultDiscount) || 0;
            }
          });
        }

        // Aggregate by class
        Object.entries(discountByStudent).forEach(([rno, discount]) => {
          const cls = sClassMap[parseInt(rno)];
          if (cls) {
            if (!discountByClass[cls]) discountByClass[cls] = 0;
            discountByClass[cls] += discount;
          }
        });

        setClassDiscounts(discountByClass);
        setClasses(classesData || []); setStudents(mergedStudents || []); setTransactions(txnData || []);

        if (reconRecord && reconRecord.expenditure) {
          const expData = reconRecord.expenditure;
          const savedOrder = expData._order || [];
          const expEntries = Object.entries(expData)
            .filter(([label]) => label !== 'Salary to Staffs' && label !== 'distribution' && label !== '_order');
          const expMap = Object.fromEntries(expEntries);
          const ordered = savedOrder.filter(label => expMap[label] !== undefined).map(label => ({ label, amount: expMap[label] || 0 }));
          const remaining = expEntries.filter(([label]) => !savedOrder.includes(label)).map(([label, amount]) => ({ label, amount: amount || 0 }));
          setExpenditures([...ordered, ...remaining]);
        } else { setExpenditures([]); }

        const savedIncomeData = reconRecord?.balance?._income;
        if (savedIncomeData) {
          const incArray = Object.entries(savedIncomeData)
            .filter(([label]) => label !== 'Fees Collection')
            .map(([label, amount]) => ({ label, amount: amount || 0 }));
          setOtherIncome(incArray);
        } else { setOtherIncome([]); }
      }
    } catch (err) {
      console.error('Error fetching reconciliation data:', err);
      setNotification({ show: true, message: 'Failed to load data.', type: 'error' });
    } finally { setLoading(false); }
  };

  const reconciliationData = useMemo(() => {
    if (isClosed || !classes.length || !students.length) return [];
    const studentsByClass = {}, allStudentsByClass = {};
    classes.forEach(c => { studentsByClass[c.class_name] = []; allStudentsByClass[c.class_name] = []; });
    students.forEach(s => {
      if (s.Class && allStudentsByClass[s.Class] !== undefined) allStudentsByClass[s.Class].push(s);
      if (s.Class && studentsByClass[s.Class] !== undefined && s.Active !== false) studentsByClass[s.Class].push(s);
    });
    const studentClassMap = {}; students.forEach(s => { studentClassMap[s.Rno] = s.Class; });
    const txnByClass = {}; classes.forEach(c => { txnByClass[c.class_name] = []; });
    transactions.forEach(t => { const cls = studentClassMap[t.student_rno]; if (cls && txnByClass[cls]) txnByClass[cls].push(t); });

    return classes.map(cls => {
      const activeStudents = studentsByClass[cls.class_name] || [];
      const allStudents = allStudentsByClass[cls.class_name] || [];
      const numStudents = activeStudents.length;
      const monthlyFee = parseFloat(cls.fee) || 0;
      const totalFee = monthlyFee * numStudents;
      const autoOutstanding = prevMonthOutstanding[cls.class_name] !== undefined
        ? prevMonthOutstanding[cls.class_name]
        : allStudents.reduce((sum, s) => { const d = parseFloat(s.FeeDues) || 0; return sum + (d < 0 ? Math.abs(d) : 0); }, 0);
      const outstanding = outstandingOverrides[cls.class_name] !== undefined ? outstandingOverrides[cls.class_name] : autoOutstanding;
      const convey = activeStudents.reduce((sum, s) => { if (!s.Transport) return sum; const sf = parseFloat(s['Transport Fee']) || 0; const cf = parseFloat(cls.transport_fee) || 0; return sum + (sf > 0 ? sf : cf); }, 0);
      const EXAM_FEE_MONTHS = [3, 7, 11];
      const examFeePerStudent = EXAM_FEE_MONTHS.includes(selectedMonth) ? (parseFloat(cls.exam_fee) || 0) : 0;
      const exam = examFeePerStudent * numStudents;
      const extra = (txnByClass[cls.class_name] || []).reduce((sum, t) => {
        const other = t.other || [];
        if (Array.isArray(other)) {
          return sum + other.reduce((mSum, item) => mSum + (parseFloat(item.value) || 0), 0);
        }
        return sum;
      }, 0);
      const discount = classDiscounts[cls.class_name] !== undefined
        ? classDiscounts[cls.class_name]
        : (txnByClass[cls.class_name] || []).reduce((sum, t) => sum + Math.abs(parseFloat(t.discount) || 0), 0);
      const income = (txnByClass[cls.class_name] || []).reduce((sum, t) => sum + (parseFloat(t.amount_paid) || 0), 0);
      // Display values (negative for money owed)
      const displayTotalFee = -totalFee;
      const displayConvey = -convey;
      const displayExam = -exam;
      const displayExtra = -extra;
      // Balance calculation: sum of all money owed (negative values) plus discount and income (they reduce what's owed)
      const balance = displayTotalFee + outstanding + displayConvey + displayExtra + displayExam + discount + income;
      return { className: cls.class_name, numStudents, monthlyFee: Math.round(monthlyFee), totalFee: Math.round(displayTotalFee), outstanding: Math.round(outstanding), convey: Math.round(displayConvey), extra: Math.round(displayExtra), exam: Math.round(displayExam), discount: Math.round(discount), income: Math.round(income), balance: Math.round(balance) };
    });
  }, [classes, students, transactions, isClosed, prevMonthOutstanding, classDiscounts, outstandingOverrides, selectedMonth]);

  const closedReconciliationData = useMemo(() => {
    if (!isClosed || !savedBalance) return [];
    return Object.entries(savedBalance).filter(([key]) => key !== 'G. Total' && key !== '_closed' && key !== '_income').map(([className, d]) => ({
      className, numStudents: d['No. St.'] || 0, monthlyFee: d['Monthly Fee'] || 0, totalFee: d['T Monthly Fee'] || 0,
      outstanding: d['O/s'] || 0, convey: d['Convey'] || 0, extra: d['Extra'] || 0, exam: d['Exam'] || 0,
      discount: d['Discount'] || 0, income: d['Income'] || 0, balance: d['Balance'] || 0
    }));
  }, [isClosed, savedBalance]);

  const displayData = isClosed ? closedReconciliationData : reconciliationData;

  const grandTotal = useMemo(() => {
    if (isClosed && savedBalance && savedBalance['G. Total']) {
      const gt = savedBalance['G. Total'];
      return { numStudents: gt['No. St.'] || 0, totalFee: gt['T Monthly Fee'] || 0, outstanding: gt['O/s'] || 0, convey: gt['Convey'] || 0, extra: gt['Extra'] || 0, exam: gt['Exam'] || 0, discount: gt['Discount'] || 0, income: gt['Income'] || 0, balance: gt['Balance'] || 0 };
    }
    return displayData.reduce((acc, row) => ({
      numStudents: acc.numStudents + row.numStudents, totalFee: acc.totalFee + row.totalFee, outstanding: acc.outstanding + row.outstanding,
      convey: acc.convey + row.convey, extra: acc.extra + row.extra, exam: acc.exam + row.exam, discount: acc.discount + row.discount,
      income: acc.income + row.income, balance: acc.balance + row.balance
    }), { numStudents: 0, totalFee: 0, outstanding: 0, convey: 0, extra: 0, exam: 0, discount: 0, income: 0, balance: 0 });
  }, [displayData, isClosed, savedBalance]);

  const expenditureTotal = useMemo(() => {
    if (isClosed && savedExpenditure) return Object.entries(savedExpenditure).filter(([k]) => k !== 'distribution').reduce((sum, [, val]) => sum + (parseInt(val, 10) || 0), 0);
    return totalStaffSalary + expenditures.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  }, [expenditures, totalStaffSalary, isClosed, savedExpenditure]);

  const otherIncomeTotal = useMemo(() => {
    return otherIncome.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  }, [otherIncome]);

  const incomeTotal = useMemo(() => {
    if (isClosed && savedIncome) return Object.entries(savedIncome).reduce((sum, [, val]) => sum + (parseInt(val, 10) || 0), 0);
    return grandTotal.income + otherIncomeTotal;
  }, [grandTotal.income, otherIncomeTotal, isClosed, savedIncome]);

  const addExpenditure = () => { if (isClosed) return; setExpenditures(prev => [...prev, { label: '', amount: 0 }]); };
  const parseFillLine = (line) => {
    const separators = [/\t/, /\s{2,}/, /[:\-|,]\s*/];
    for (const sep of separators) {
      const parts = line.split(sep).map(s => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        const last = parts[parts.length - 1].replace(/[^0-9.]/g, '');
        const amount = parseFloat(last);
        if (!isNaN(amount)) {
          const label = parts.slice(0, parts.length - 1).join(' ');
          return { label, amount };
        }
      }
    }
    return null;
  };
  const fillParsedLines = useMemo(() => {
    if (!fillText.trim()) return [];
    return fillText.split('\n').map((line, i) => {
      if (!line.trim()) return { line, index: i, valid: null };
      const parsed = parseFillLine(line);
      return { line, index: i, valid: !!parsed, parsed };
    });
  }, [fillText]);
  const handleFillSubmit = () => {
    const parsed = fillParsedLines.filter(l => l.valid && l.parsed).map(l => l.parsed);
    if (parsed.length > 0) {
      setExpenditures(prev => [...prev, ...parsed]);
      setNotification({ show: true, message: `Added ${parsed.length} expenditure(s).`, type: 'success' });
    } else {
      setNotification({ show: true, message: 'Could not parse any rows. Use format: Label  Amount (one per line)', type: 'warning' });
    }
    setFillText('');
    setShowFillModal(false);
  };
  const updateExpenditure = (index, field, value) => { if (isClosed) return; setExpenditures(prev => prev.map((item, i) => i === index ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : item)); };
  const removeExpenditure = (index) => { if (isClosed) return; setExpenditures(prev => prev.filter((_, i) => i !== index)); };
  const copyFromLastMonth = async () => {
    if (isClosed) return;
    try {
      const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
      const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
      const { data: prevRecord } = await supabase.from('reconciliation').select('expenditure').eq('year', prevYear).eq('month', prevMonth).single();
      if (prevRecord && prevRecord.expenditure) {
        const expData = prevRecord.expenditure;
        const savedOrder = expData._order || [];
        const expEntries = Object.entries(expData)
          .filter(([label]) => label !== 'Salary to Staffs' && label !== 'distribution' && label !== '_order');
        const expMap = Object.fromEntries(expEntries);
        const ordered = savedOrder.filter(label => expMap[label] !== undefined).map(label => ({ label, amount: expMap[label] || 0 }));
        const remaining = expEntries.filter(([label]) => !savedOrder.includes(label)).map(([label, amount]) => ({ label, amount: amount || 0 }));
        setExpenditures([...ordered, ...remaining]);
        setNotification({ show: true, message: 'Expenditure copied from last month.', type: 'success' });
      } else {
        setNotification({ show: true, message: 'No expenditure found in last month.', type: 'warning' });
      }
    } catch (err) {
      console.error('Error copying from last month:', err);
      setNotification({ show: true, message: 'Failed to copy from last month.', type: 'error' });
    }
  };
  const addOtherIncome = () => { if (isClosed) return; setOtherIncome(prev => [...prev, { label: '', amount: 0 }]); };
  const updateOtherIncome = (index, field, value) => { if (isClosed) return; setOtherIncome(prev => prev.map((item, i) => i === index ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : item)); };
  const removeOtherIncome = (index) => { if (isClosed) return; setOtherIncome(prev => prev.filter((_, i) => i !== index)); };

  const computedSalaryTotal = useMemo(() => {
    return staffList.reduce((sum, staff) => {
      const dist = salaryDistribution[staff.name] || { salary: staff.salary, present: daysInMonth };
      const present = dist.present || 0;
      // Rule: If 1 day leave or less (29 or 30 present), pay full salary (30 days)
      // If more than 1 day leave, pay for (present + 1) days
      let payableDays;
      if (present >= daysInMonth - 1) {
        // 29 or 30 days present: pay full salary
        payableDays = daysInMonth;
      } else {
        // Less than 29 days present: pay for (present + 1) days
        payableDays = Math.min(daysInMonth, present + 1);
      }
      const extra = parseInt(dist.extra, 10) || 0;
      return sum + Math.round((dist.salary || 0) / daysInMonth * payableDays) - (parseInt(dist.fine, 10) || 0) + extra;
    }, 0);
  }, [salaryDistribution, daysInMonth, staffList]);

  const handleDistributionChange = (name, field, value) => {
    setSalaryDistribution(prev => ({
      ...prev,
      [name]: { ...prev[name], [field]: ['present', 'salary', 'fine', 'extra'].includes(field) ? parseInt(value, 10) || 0 : value }
    }));
  };

  const handleSalarySubmit = () => {
    setTotalStaffSalary(computedSalaryTotal);
    setShowSalaryModal(false);
  };

  const buildExpenditureJson = () => {
    const obj = { 'Salary to Staffs': totalStaffSalary, distribution: salaryDistribution };
    expenditures.forEach(item => { if (item.label) obj[item.label] = item.amount || 0; });
    obj._order = expenditures.filter(item => item.label).map(item => item.label);
    return obj;
  };

  const buildIncomeJson = () => {
    const obj = { 'Fees Collection': grandTotal.income };
    otherIncome.forEach(item => { if (item.label) obj[item.label] = item.amount || 0; });
    return obj;
  };

  const buildBalanceJson = () => {
    const obj = {};
    reconciliationData.forEach(row => {
      obj[row.className] = { 'No. St.': row.numStudents, 'Monthly Fee': row.monthlyFee, 'T Monthly Fee': row.totalFee, 'O/s': row.outstanding, 'Convey': row.convey, 'Extra': row.extra, 'Exam': row.exam, 'Discount': row.discount, 'Income': row.income, 'Balance': row.balance };
    });
    obj['G. Total'] = { 'No. St.': grandTotal.numStudents, 'T Monthly Fee': grandTotal.totalFee, 'O/s': grandTotal.outstanding, 'Convey': grandTotal.convey, 'Extra': grandTotal.extra, 'Exam': grandTotal.exam, 'Discount': grandTotal.discount, 'Income': grandTotal.income, 'Balance': grandTotal.balance };
    obj['_income'] = buildIncomeJson();
    if (openingBalance !== null) obj['_openingBalance'] = openingBalance;
    return obj;
  };

  const saveExpenditure = async () => {
    try {
      setActionLoading(true);
      const balanceData = buildBalanceJson();
      const { error } = await supabase.from('reconciliation').upsert({ year: selectedYear, month: selectedMonth, balance: balanceData, expenditure: buildExpenditureJson(), updated_at: new Date().toISOString() }, { onConflict: 'year,month' });
      if (error) throw error;
      setNotification({ show: true, message: 'Data saved.', type: 'success' });
    } catch (err) { console.error('Error saving:', err); setNotification({ show: true, message: 'Failed to save.', type: 'error' }); }
    finally { setActionLoading(false); }
  };

  const handleCloseMonth = async () => {
    const key = `${selectedYear}-${selectedMonth}`;
    if (closedMonths.includes(key)) return;
    try {
      setActionLoading(true);
      const balanceData = { ...buildBalanceJson(), _closed: true };
      const { error } = await supabase.from('reconciliation').upsert({ year: selectedYear, month: selectedMonth, balance: balanceData, expenditure: buildExpenditureJson(), updated_at: new Date().toISOString() }, { onConflict: 'year,month' });
      if (error) throw error;
      setClosedMonths(prev => [...prev, key]);
      setSavedBalance(balanceData);
      setSavedExpenditure(buildExpenditureJson());
      setSavedIncome(balanceData._income || {});
      setShowCloseConfirm(false);
      setNotification({ show: true, message: `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} closed.`, type: 'success' });
    } catch (err) { console.error('Error closing:', err); setNotification({ show: true, message: 'Failed to close.', type: 'error' }); }
    finally { setActionLoading(false); }
  };

  const handleReopenMonth = async () => {
    const key = `${selectedYear}-${selectedMonth}`;
    if (!lastClosed || key !== lastClosed.key) return;
    try {
      setActionLoading(true);
      const { data: currentRecord } = await supabase.from('reconciliation').select('balance').eq('year', selectedYear).eq('month', selectedMonth).single();
      const reopenedBalance = currentRecord?.balance ? { ...currentRecord.balance } : null;
      if (reopenedBalance) delete reopenedBalance._closed;
      const { error } = await supabase.from('reconciliation').update({ balance: reopenedBalance, updated_at: new Date().toISOString() }).eq('year', selectedYear).eq('month', selectedMonth);
      if (error) throw error;
      setClosedMonths(prev => prev.filter(k => k !== key));
      setSavedBalance(null); setSavedExpenditure(null); setSavedIncome(null);
      setShowReopenConfirm(false);
      setNotification({ show: true, message: `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} reopened.`, type: 'success' });
      fetchData();
    } catch (err) { console.error('Error reopening:', err); setNotification({ show: true, message: 'Failed to reopen.', type: 'error' }); }
    finally { setActionLoading(false); }
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  // Closed month expenditure rows for display
  const closedExpRows = useMemo(() => {
    if (!isClosed || !savedExpenditure) return [];
    return Object.entries(savedExpenditure).filter(([label]) => label !== 'distribution').map(([label, amount]) => ({ label, amount }));
  }, [isClosed, savedExpenditure]);

  const closedIncomeTotal = isClosed ? grandTotal.income : 0;

  return (
    <div className="reconciliation-container">
      {notification && notification.show && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="reconciliation-header">
        <div className="reconciliation-title">
          <FaBalanceScale className="recon-title-icon" />
          <div><h2>Reconciliation</h2><p className="recon-subtitle">Monthly fee reconciliation report</p></div>
        </div>
      </div>

      <div className='reconciliation-filters-container'>
        <div className="reconciliation-filters">
          <div className="recon-filter-group">
            <select value={selectedMonth || ''} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
              {MONTH_NAMES.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
            </select>
          </div>
          <div className="recon-filter-group">
            <select value={selectedYear || ''} onChange={e => setSelectedYear(parseInt(e.target.value))}>
              {yearOptions.map(y => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
        </div>
        <div className="recon-filter-actions">
          {!isClosed && (
            <button className="recon-save-btn" onClick={saveExpenditure} disabled={actionLoading || loading}>
              {actionLoading ? 'Saving...' : 'Save'}
            </button>
          )}
          {isClosed ? (
            lastClosed && `${selectedYear}-${selectedMonth}` === lastClosed.key ? (
              <button className="recon-reopen-btn" onClick={() => setShowReopenConfirm(true)} disabled={loading}><FaUnlock /> Reopen {MONTH_NAMES[selectedMonth - 1]}</button>
            ) : (<span className="recon-closed-badge"><FaLock /> Closed</span>)
          ) : (
            <button className="recon-close-btn" onClick={() => setShowCloseConfirm(true)} disabled={loading}><FaLock /> Close {MONTH_NAMES[selectedMonth - 1]}</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="reconciliation-loading"><FaSpinner className="recon-spin" /><span>Loading reconciliation data...</span></div>
      ) : (
        <div className="reconciliation-table-wrap">
          <table className="reconciliation-table">
            <thead>
              <tr>
                <th>Classes</th><th>No. St.</th><th>Monthly Fee</th><th>T Monthly Fee</th><th>O/s</th><th>Convey</th><th>Extra</th><th>Exam</th><th>Discount</th><th>Income</th><th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((row) => (
                <tr key={row.className}>
                  <td className="recon-class-cell">{row.className}</td>
                  <td>{row.numStudents}</td><td>{row.monthlyFee}</td><td>{row.totalFee}</td>
                  <td>{isClosed ? row.outstanding : (
                    <input type="number" className="recon-exp-input" value={row.outstanding} style={{ width: '100px', textAlign: 'center' }}
                      onChange={e => setOutstandingOverrides(prev => ({ ...prev, [row.className]: parseFloat(e.target.value) || 0 }))} />
                  )}</td>
                  <td>{row.convey}</td>
                  <td>{row.extra}</td>
                  <td>{row.exam}</td><td>{row.discount}</td><td>{row.income}</td>
                  <td className={`recon-balance-cell ${row.balance < 0 ? 'recon-negative' : row.balance > 0 ? 'recon-positive' : ''}`}>{row.balance}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="recon-grand-total-row">
                <td><strong>G. Total</strong></td><td><strong>{grandTotal.numStudents}</strong></td><td></td>
                <td><strong>{grandTotal.totalFee}</strong></td><td><strong>{grandTotal.outstanding}</strong></td><td><strong>{grandTotal.convey}</strong></td>
                <td><strong>{grandTotal.extra}</strong></td><td><strong>{grandTotal.exam}</strong></td><td><strong>{grandTotal.discount}</strong></td>
                <td><strong>{grandTotal.income}</strong></td>
                <td className={`recon-balance-cell ${grandTotal.balance < 0 ? 'recon-negative' : grandTotal.balance > 0 ? 'recon-positive' : ''}`}><strong>{grandTotal.balance}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!loading && (
        <>
        <div className="recon-expenditure-section" style={{ marginBottom: '24px' }}>
          <div className="recon-expenditure-header">
            <h3>Income</h3>
            {!isClosed && (<button className="recon-add-exp-btn" onClick={addOtherIncome}><FaPlus /> Add Row</button>)}
          </div>

          {isClosed ? (
            savedIncome && Object.keys(savedIncome).length > 0 ? (
              <div className="reconciliation-table-wrap">
                <table className="reconciliation-table">
                  <thead><tr><th style={{ textAlign: 'left' }}>Label</th><th>Amount (₹)</th></tr></thead>
                  <tbody>
                    {Object.entries(savedIncome).map(([label, amount], idx) => (
                      <tr key={idx} style={label === 'Fees Collection' ? { backgroundColor: '#f0fff4' } : {}}>
                        <td style={{ textAlign: 'left', fontWeight: label === 'Fees Collection' ? '600' : '400' }}>{label}</td>
                        <td style={{ fontWeight: label === 'Fees Collection' ? '600' : '400' }}>{amount}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="recon-grand-total-row"><td><strong>Total</strong></td><td><strong>{incomeTotal}</strong></td></tr></tfoot>
                </table>
              </div>
            ) : (<p className="recon-no-exp">No income recorded.</p>)
          ) : (
            <div className="reconciliation-table-wrap">
              <table className="reconciliation-table">
                <thead><tr><th style={{ textAlign: 'left' }}>Label</th><th>Amount (₹)</th><th style={{ width: '50px' }}></th></tr></thead>
                <tbody>
                  <tr style={{ backgroundColor: '#f0fff4' }}>
                    <td style={{ textAlign: 'left', fontWeight: '600' }}>Fees Collection</td>
                    <td style={{ fontWeight: '600' }}>{grandTotal.income}</td>
                    <td></td>
                  </tr>
                  {otherIncome.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ textAlign: 'left' }}>
                        <input type="text" className="recon-exp-input recon-exp-label" value={item.label} onChange={e => updateOtherIncome(idx, 'label', e.target.value)} placeholder="e.g. Donation" />
                      </td>
                      <td>
                        <input type="number" className="recon-exp-input recon-exp-amount" value={item.amount || ''} onChange={e => updateOtherIncome(idx, 'amount', e.target.value)} placeholder="0" />
                      </td>
                      <td><button className="recon-remove-exp-btn" onClick={() => removeOtherIncome(idx)}><FaTrash /></button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="recon-grand-total-row"><td><strong>Total</strong></td><td><strong>{incomeTotal}</strong></td><td></td></tr></tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="recon-expenditure-section">
          <div className="recon-expenditure-header">
            <h3>Expenditure</h3>
            {!isClosed && (
              <div className="recon-exp-actions">
                <button className="recon-add-exp-btn" onClick={copyFromLastMonth} style={{ marginRight: '8px' }}>Copy from Last Month</button>
                <button className="recon-add-exp-btn" onClick={() => setShowFillModal(true)} style={{ marginRight: '8px' }}><FaEdit /> Fill</button>
                <button className="recon-add-exp-btn" onClick={addExpenditure}><FaPlus /> Add Row</button>
              </div>
            )}
          </div>

          {isClosed ? (
            closedExpRows.length > 0 ? (
              <div className="reconciliation-table-wrap">
                <table className="reconciliation-table">
                  <thead><tr>
                    <th style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none' }} onClick={() => setExpSort(prev => ({ field: 'label', direction: prev.field === 'label' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Label {expSort.field === 'label' ? (expSort.direction === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setExpSort(prev => ({ field: 'amount', direction: prev.field === 'amount' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Amount (₹) {expSort.field === 'amount' ? (expSort.direction === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  </tr></thead>
                  <tbody>
                    {(expSort.field
                      ? closedExpRows.map((item, idx) => ({ item, idx })).sort((a, b) => {
                          let cmp = 0;
                          if (expSort.field === 'label') cmp = (a.item.label || '').localeCompare(b.item.label || '');
                          else cmp = (parseFloat(a.item.amount) || 0) - (parseFloat(b.item.amount) || 0);
                          return expSort.direction === 'asc' ? cmp : -cmp;
                        })
                      : closedExpRows.map((item, idx) => ({ item, idx }))
                    ).map(({ item, idx }) => (
                      <tr key={idx} style={item.label === 'Salary to Staffs' ? { backgroundColor: '#f0f4ff' } : {}}>
                        <td style={{ textAlign: 'left', fontWeight: item.label === 'Salary to Staffs' ? '600' : '400' }}>{item.label}</td>
                        <td style={{ fontWeight: item.label === 'Salary to Staffs' ? '600' : '400' }}>{item.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="recon-grand-total-row"><td><strong>Total</strong></td><td><strong>{expenditureTotal}</strong></td></tr></tfoot>
                </table>
              </div>
            ) : (<p className="recon-no-exp">No expenditures recorded.</p>)
          ) : (
            (expenditures.length > 0 || totalStaffSalary > 0) ? (
              <div className="reconciliation-table-wrap">
                <table className="reconciliation-table">
                  <thead><tr>
                    <th style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none' }} onClick={() => setExpSort(prev => ({ field: 'label', direction: prev.field === 'label' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Label {expSort.field === 'label' ? (expSort.direction === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setExpSort(prev => ({ field: 'amount', direction: prev.field === 'amount' && prev.direction === 'asc' ? 'desc' : 'asc' }))}>
                      Amount (₹) {expSort.field === 'amount' ? (expSort.direction === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ width: '50px' }}></th>
                  </tr></thead>
                  <tbody>
                    <tr style={{ backgroundColor: '#f0f4ff' }}>
                      <td style={{ textAlign: 'left', fontWeight: '600' }}>Salary to Staffs</td>
                      <td style={{ fontWeight: '600' }}>{totalStaffSalary}</td>
                      <td><button className="recon-remove-exp-btn" onClick={() => setShowSalaryModal(true)} title="Edit salary distribution"><FaEdit /></button></td>
                    </tr>
                    {(expSort.field
                      ? expenditures.map((item, idx) => ({ item, idx })).sort((a, b) => {
                          let cmp = 0;
                          if (expSort.field === 'label') cmp = (a.item.label || '').localeCompare(b.item.label || '');
                          else cmp = (parseFloat(a.item.amount) || 0) - (parseFloat(b.item.amount) || 0);
                          return expSort.direction === 'asc' ? cmp : -cmp;
                        })
                      : expenditures.map((item, idx) => ({ item, idx }))
                    ).map(({ item, idx }) => (
                      <tr key={idx}>
                        <td style={{ textAlign: 'left' }}>
                          <input type="text" className="recon-exp-input recon-exp-label" value={item.label} onChange={e => updateExpenditure(idx, 'label', e.target.value)} placeholder="e.g. Electricity" />
                        </td>
                        <td>
                          <input type="number" className="recon-exp-input recon-exp-amount" value={item.amount || ''} onChange={e => updateExpenditure(idx, 'amount', e.target.value)} placeholder="0" />
                        </td>
                        <td><button className="recon-remove-exp-btn" onClick={() => removeExpenditure(idx)}><FaTrash /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="recon-grand-total-row"><td><strong>Total</strong></td><td><strong>{expenditureTotal}</strong></td><td></td></tr></tfoot>
                </table>
              </div>
            ) : (<p className="recon-no-exp">No expenditures added.</p>)
          )}

          <div className="recon-final-calc">
            <div className="recon-final-row">
              <span>Opening Balance (Last Month Net)</span>
              {isClosed ? (
                <span className={`recon-final-value ${(openingBalance || 0) < 0 ? 'recon-negative' : 'recon-positive'}`}>
                  ₹{(openingBalance || 0).toLocaleString('en-IN')}
                </span>
              ) : (
                <input
                  type="number"
                  className="recon-exp-input"
                  value={openingBalance ?? ''}
                  onChange={e => setOpeningBalance(e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                  placeholder="Auto from last month"
                  style={{ width: '120px', textAlign: 'right' }}
                />
              )}
            </div>
            <div className="recon-final-row"><span>Total Income</span><span className="recon-final-value">₹{incomeTotal.toLocaleString('en-IN')}</span></div>
            <div className="recon-final-row"><span>Total Expenditure</span><span className="recon-final-value">₹{expenditureTotal.toLocaleString('en-IN')}</span></div>
            <div className={`recon-final-row recon-final-result ${((openingBalance || 0) + incomeTotal - expenditureTotal) < 0 ? 'recon-negative' : 'recon-positive'}`}>
              <span>Net</span>
              <span className="recon-final-value">₹{((openingBalance || 0) + incomeTotal - expenditureTotal).toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
        </>
      )}

      {showCloseConfirm && (
        <div className="recon-modal-backdrop" onClick={() => !actionLoading && setShowCloseConfirm(false)}>
          <div className="recon-modal" onClick={e => e.stopPropagation()}>
            <h4>Close {MONTH_NAMES[selectedMonth - 1]} {selectedYear}?</h4>
            <p>This will snapshot totals and lock editing for this month.</p>
            <div className="recon-modal-actions">
              <button className="recon-btn cancel" onClick={() => setShowCloseConfirm(false)} disabled={actionLoading}>Cancel</button>
              <button className="recon-btn confirm" onClick={handleCloseMonth} disabled={actionLoading}>{actionLoading ? 'Closing...' : 'Confirm Close'}</button>
            </div>
          </div>
        </div>
      )}

      {showSalaryModal && (
        <div className="recon-modal-backdrop" onClick={() => setShowSalaryModal(false)}>
          <div className="recon-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}>
            <h4>Salary Distribution - {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</h4>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '12px' }}>Days in month: <strong>{daysInMonth}</strong></p>
            <table className="reconciliation-table" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Staff Name</th>
                  <th>Role</th>
                  <th>Monthly Salary</th>
                  <th>Per Day</th>
                  <th style={{ width: '80px' }}>Days Present</th>
                  <th>Fine</th>
                  <th>Extra</th>
                  <th>Payable</th>
                  <th style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {staffList.map(staff => {
                  const dist = salaryDistribution[staff.name] || { salary: staff.salary, present: daysInMonth, fine: 0 };
                  const perDay = Math.round(dist.salary / daysInMonth);
                  const present = dist.present || 0;
                  const fine = parseInt(dist.fine, 10) || 0;
                  const extra = parseInt(dist.extra, 10) || 0;
                  const payableDays = present >= daysInMonth - 1 ? daysInMonth : Math.min(daysInMonth, present + 1);
                  const payable = Math.round((dist.salary / daysInMonth) * payableDays) - fine + extra;
                  return (
                    <tr key={staff.staff_no}>
                      <td style={{ textAlign: 'left' }}>{staff.name}</td>
                      <td>{staff.role}</td>
                      <td>
                        <input type="number" className="recon-extra-input" value={dist.salary} min="0"
                          onChange={e => handleDistributionChange(staff.name, 'salary', e.target.value)}
                          style={{ width: '100px', textAlign: 'center' }} />
                      </td>
                      <td>{perDay}</td>
                      <td>
                        <input type="number" className="recon-extra-input" value={dist.present} min="0" max={daysInMonth}
                          onChange={e => handleDistributionChange(staff.name, 'present', e.target.value)}
                          style={{ width: '60px', textAlign: 'center' }} />
                      </td>
                      <td>
                        <input type="number" className="recon-extra-input" value={fine} min="0"
                          onChange={e => handleDistributionChange(staff.name, 'fine', e.target.value)}
                          style={{ width: '70px', textAlign: 'center' }} />
                      </td>
                      <td>
                        <input type="number" className="recon-extra-input" value={extra || ''} placeholder="0"
                          onChange={e => handleDistributionChange(staff.name, 'extra', e.target.value)}
                          style={{ width: '70px', textAlign: 'center' }} />
                      </td>
                      <td style={{ fontWeight: '600' }}>{payable}</td>
                      <td>
                        <button className="recon-remove-exp-btn" title="Remove staff from distribution"
                          onClick={() => {
                            setStaffList(prev => prev.filter(s => s.staff_no !== staff.staff_no));
                            setSalaryDistribution(prev => { const next = { ...prev }; delete next[staff.name]; return next; });
                          }}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="recon-grand-total-row">
                  <td><strong>Total</strong></td>
                  <td><strong>{staffList.length}</strong></td>
                  <td><strong>{staffList.reduce((sum, s) => sum + ((salaryDistribution[s.name]?.salary) || s.salary), 0)}</strong></td>
                  <td></td>
                  <td></td>
                  <td><strong>{staffList.reduce((sum, s) => sum + (parseInt(salaryDistribution[s.name]?.fine, 10) || 0), 0)}</strong></td>
                  <td><strong>{staffList.reduce((sum, s) => sum + (parseInt(salaryDistribution[s.name]?.extra, 10) || 0), 0)}</strong></td>
                  <td><strong>{computedSalaryTotal}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <div className="recon-modal-actions" style={{ marginTop: '16px' }}>
              <button className="recon-btn cancel" onClick={() => setShowSalaryModal(false)}>Cancel</button>
              <button className="recon-btn confirm" onClick={handleSalarySubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {showFillModal && (
        <div className="recon-modal-backdrop" onClick={() => setShowFillModal(false)}>
          <div className="recon-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h4>Fill Expenditures</h4>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '12px' }}>
              Paste one expenditure per line. Supported formats:<br />
              <code style={{ fontSize: '0.8rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>Label  Amount</code> &nbsp;
              <code style={{ fontSize: '0.8rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>Label, Amount</code> &nbsp;
              <code style={{ fontSize: '0.8rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>Label: Amount</code>
            </p>
            <textarea
              value={fillText}
              onChange={e => setFillText(e.target.value)}
              placeholder={"Electricity  2500\nWater Bill  800\nStationery, 1200\nMisc: 500"}
              rows={8}
              style={{ width: '100%', padding: '10px', fontSize: '0.9rem', fontFamily: 'monospace', border: '2px solid #e0e0e0', borderRadius: '8px', resize: 'vertical', boxSizing: 'border-box' }}
            />
            {fillText.trim() && (
              <div style={{ marginTop: '10px', maxHeight: '180px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px' }}>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '6px', fontWeight: 600 }}>Preview:</p>
                {fillParsedLines.map((item, i) => {
                  if (item.valid === null) return null;
                  return (
                    <div key={i} style={{
                      padding: '4px 8px', marginBottom: '3px', borderRadius: '4px', fontSize: '0.85rem', fontFamily: 'monospace',
                      backgroundColor: item.valid ? '#ecfdf5' : '#fef2f2',
                      color: item.valid ? '#065f46' : '#991b1b',
                      border: `1px solid ${item.valid ? '#a7f3d0' : '#fecaca'}`
                    }}>
                      {item.valid
                        ? <span>✓ <strong>{item.parsed.label}</strong> → ₹{item.parsed.amount}</span>
                        : <span>✗ {item.line} <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>(invalid format)</span></span>
                      }
                    </div>
                  );
                })}
              </div>
            )}
            <div className="recon-modal-actions" style={{ marginTop: '12px' }}>
              <button className="recon-btn cancel" onClick={() => { setShowFillModal(false); setFillText(''); }}>Cancel</button>
              <button className="recon-btn confirm" onClick={handleFillSubmit}
                disabled={!fillText.trim() || fillParsedLines.filter(l => l.valid).length === 0}>
                Add {fillParsedLines.filter(l => l.valid).length} Expenditure(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {showReopenConfirm && (
        <div className="recon-modal-backdrop" onClick={() => !actionLoading && setShowReopenConfirm(false)}>
          <div className="recon-modal" onClick={e => e.stopPropagation()}>
            <h4>Reopen {MONTH_NAMES[selectedMonth - 1]} {selectedYear}?</h4>
            <p>This will unlock editing and mark the month as open.</p>
            <div className="recon-modal-actions">
              <button className="recon-btn cancel" onClick={() => setShowReopenConfirm(false)} disabled={actionLoading}>Cancel</button>
              <button className="recon-btn confirm warn" onClick={handleReopenMonth} disabled={actionLoading}>{actionLoading ? 'Reopening...' : 'Confirm Reopen'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reconciliation;
